// fx registry — the catalog of live effects the fxRender seam can run.
//
// A step in obj.data.fx is {id, params}; this registry maps id → the effect
// DEFINITION. Definitions are plain data + pure functions, no fabric imports:
// the registry must stay loadable anywhere (probes, workers, the seam's
// canary) without dragging the editor in.
//
// kind: 'filter' — canvas→canvas pure function over the object's own raster.
//   The seam renders the object's base _render into an offscreen at device
//   resolution, hands it through each step's apply, and draws the result back
//   in object units. Filters MUST be pure pixels: no fabric object mutation,
//   no retained references to env or scratch canvases beyond the call.
//
// kind: 'source' — REPLACES the base raster instead of filtering it (Phase B:
//   live 3D extrude). Async-only:
//     applyAsync(baseCanvas, env) → Promise<{canvas, halfW, halfH, ax, ay}>
//       canvas  the replacement raster (content over transparency). It need
//               not match the glyph box — rotation/extrusion overhang is
//               expected. It carries its OWN object-unit mapping:
//       halfW/halfH  half the canvas's extent in OBJECT UNITS — the canvas
//               maps onto the object-space rect [±halfW, ±halfH]…
//       ax/ay   …shifted so the step's anchor point (extrude: the projected
//               front-face center) lands on the object's center: the seam
//               draws the canvas at (-halfW − ax, -halfH − ay).
//       A null/undefined result means "keep the incoming raster" (failure
//       fallback — the seam stashes the flat base and reports once).
//   NO applySync, ever: the seam's sync pass draws the last stash when its
//   hash still matches, else the plain base render (flat while typing; the
//   debounced async upgrade brings the 3D back). While obj.isEditing the
//   seam never invokes a source step at all (no WebGL mid-edit).
//   consumes: ['bevel', …] — CONSUMED-FILTER contract (composition, owner
//   2026-07-16): filter ids whose LOOK this source step renders itself when
//   it is active (non-empty). The seam then skips those filter steps in
//   every chain (sync, full, margins) — the filter must not ALSO shade the
//   replacement raster — while the steps stay untouched in data.fx with
//   their original params: remove the source step and the filters run live
//   again exactly as saved. The SOURCE declares consumption (not a flag on
//   the filter) because the mapping knowledge lives with the consumer:
//   fx 'extrude' reads the sibling 'bevel' step's depth and re-expresses it
//   as geometric edge rounding under the 3D lights (see fxExtrude.js).
//   bounds(params, geo) with geo = {u, w, h, obj} → {marginX, marginY}: an
//   HONEST over-estimate (object units, per side, per axis) of how far the
//   replacement may paint past the object's tight box. Anisotropic on
//   purpose — rotation + perspective grow x and y differently, which is why
//   source steps do not use the scalar spill. Undersize it and the cache
//   clips; oversize just costs cache pixels. geo.obj (additive, 3D-controls
//   lane) is the live fabric object, READ ONLY, and may be undefined: source
//   steps that re-express NATIVE-prop effects in the bake (extrude renders
//   the Outline effect as a rim around the 3D silhouette) size that spill
//   from the object's own props — params cannot see them.
//   Export: source steps cannot run during the synchronous export render —
//   exportWorkspaceBlob pre-awaits a full-resolution bake per object
//   (fxPrimeExport) and the seam draws that primed stash.

const REGISTRY = new Map();

/**
 * Register an effect definition.
 * @param {object} def
 *   id        string — the step id persisted in data.fx (stable forever).
 *   version   number — bump when the same params render differently.
 *   kind      'filter' (Phase A) | 'source' (Phase B, reserved).
 *   sanitize  (params) → params — clamp/default; the seam and the facade both
 *             run it, so a hand-edited JSON can never crash a render.
 *   isEmpty   (params) → bool — true when the step is a visual no-op and the
 *             facade should drop it (bevel: depth <= 0).
 *   spill     (params, u) → object-unit margin the effect may paint OUTSIDE
 *             the object's tight box (u = fontSize/100 for text, else
 *             min(w,h)/100). Drives the cache-dims override and the seam's
 *             offscreen padding — undersize it and the effect clips. For
 *             steps that also declare spillXY, this scalar keeps ONE duty:
 *             the viewport-patch guard margin (the neighborhood a region
 *             bake must over-render for edge-coherent shading).
 *   spillXY   (params, {u, w, h, obj}) → {marginX, marginY} — OPTIONAL,
 *             filters only (sticker-shapes lane): anisotropic render/cache
 *             margins for filters whose reach depends on the object's box
 *             aspect (a circle badge around a wide word overhangs top and
 *             bottom far more than the sides). When declared it REPLACES the
 *             scalar spill in the seam's margin math; same read-only geometry
 *             bag the source bounds() gets. Honest over-estimates only.
 *   bounds    (params, {u, w, h}) → {marginX, marginY} — source steps only
 *             (see the kind:'source' notes above). Defaults to zero margins.
 *   consumes  string[] — source steps only: filter ids this step renders
 *             itself while active; the seam skips them (see the contract
 *             notes above). Defaults to [].
 *   applySync (canvas, env) → canvas — the FAST path, budget ~12ms at a
 *             typical 800×300-device-px text (run at reduced internal
 *             resolution if needed; soft-for-a-beat is fine, slow is not).
 *   applyAsync(canvas, env) → Promise<canvas> — optional full-quality path;
 *             the seam debounces it and stashes the result. Defaults to
 *             applySync with env.quality = 'full'.
 *
 * env = { scale     device px per object unit at the incoming ctx,
 *         u         recipe unit in object units (fontSize/100 or min(w,h)/100),
 *         obj       the fabric object (READ ONLY — filters must not mutate),
 *         width     offscreen width in device px,
 *         height    offscreen height in device px,
 *         quality   'fast' | 'full',
 *         params    THIS step's sanitized params (set by the seam per step),
 *         getScratch(w, h) → {canvas, ctx} pooled scratch (do not retain),
 *         frame     {x0, y0, effX, effY} object↔canvas mapping: canvas px =
 *                   (objUnits − x0/y0)·effX/effY — one formula for full and
 *                   region renders (sticker-shapes lane),
 *         renderVariant(kind) → {canvas, W, H} | undefined — memoized second
 *                   base passes, PRESENT only when the object's stroke-slot
 *                   effect needs one; a kind that is not offered returns
 *                   undefined, so callers must guard the result:
 *                   'noStroke' (layer separation, 2026-07-20) — stroke
 *                   suppressed: the glyph-only letters layer, offered for
 *                   the band-over-opaque-glyphs family (glyphMaskWanted).
 *                   'filledSolid' (union-contour lane, 2026-07-20) — fill
 *                   forced to an opaque solid, stroke suppressed,
 *                   backgrounds/decorations excluded: the TRUE UNION
 *                   letterform alpha, offered for the contourOutline family
 *                   (hollow/trace — fxContourOutline traces it).
 *                   Absent ⇒ single-layer object; run the classic path.
 *
 *                   OBJECT-SILHOUETTE CONTRACT (universal-effects U1 —
 *                   binding for U3's extrude source input): on SHAPE and
 *                   ICON kinds (capabilityMatrix.objectKindOf) BOTH variant
 *                   kinds resolve to ONE memoized SILHOUETTE raster in the
 *                   same {canvas, W, H} envelope, same transform/region as
 *                   the base:
 *                     shape — its own path/geometry rendered fill-parked
 *                       solid, stroke parked off (the Border tool's stroke
 *                       is CONTENT, not silhouette; live paint — gradients,
 *                       transparent fills — is irrelevant: alpha is the
 *                       target).
 *                     icon  — a pristine second base pass of the raster:
 *                       its ALPHA IS the silhouette (the isIcon tint
 *                       pipeline bakes color, never alpha). Never a reuse
 *                       of the chain canvas — filters mutate src in place.
 *                   Offered only while a consumer is live (sticker family /
 *                   contourBand step / the 'extrude' source step — U3).
 *                   U3's silhouetteOf(obj) consumes exactly this: shapes
 *                   cut exact vectors (fabric geometry → THREE.Shape,
 *                   text3d/silhouette3d) and the traced-icon path reads
 *                   this variant through fxContourOutline's extraction, so
 *                   the contract here is the one source of silhouette
 *                   truth. }
 */
export function registerFx(def) {
  if (!def || typeof def.id !== 'string' || !def.id) {
    throw new Error('registerFx: def.id required');
  }
  if (typeof def.applySync !== 'function' && typeof def.applyAsync !== 'function') {
    throw new Error(`registerFx(${def.id}): applySync or applyAsync required`);
  }
  if (def.kind === 'source' && typeof def.applySync === 'function') {
    // The whole point of 'source' is that it never runs synchronously — an
    // applySync here would run WebGL-scale work per keystroke.
    throw new Error(`registerFx(${def.id}): source steps must not define applySync`);
  }
  REGISTRY.set(def.id, {
    version: 1,
    kind: 'filter',
    sanitize: (p) => p || {},
    isEmpty: () => false,
    spill: () => 0,
    bounds: () => ({ marginX: 0, marginY: 0 }),
    consumes: [],
    ...def,
  });
  return def;
}

export function getFx(id) {
  return REGISTRY.get(id) || null;
}

/** Registered ids — canary/debug surface, not a public API. */
export function listFx() {
  return [...REGISTRY.keys()];
}
